import type { ChecklistReasonInput, Message } from '../../api/types'
import { formatClock } from '../../lib/format'
import type { TimelineMessage } from '../../sync/outboxMerge'

/** A bot's question with answers to tap (docs/superpowers/specs/2026-10-03-option-messages-design.md). */
export function isQuestion(message: Message): boolean {
  return Boolean(message.options?.length) && !message.deletedAt
}

/**
 * Which option this device answered each question with, from the answers in the conversation, sent or still in the outbox (a
 * checklist's Done has no option: null):
 * a question locks the moment it is tapped.
 */
export function localAnswers(timeline: readonly TimelineMessage[]): Map<string, string | null> {
  const answers = new Map<string, string | null>()
  for (const { message } of timeline) {
    if (message.answer) answers.set(message.answer.messageId, message.answer.optionId)
  }
  return answers
}

export type QuestionStatus = 'open' | 'answered' | 'closed'

/** An answer counts whatever happened after it; otherwise closing, expiry and a bot that cannot hear all close the question. */
export function questionStatus(q: { chosen: string | null; closedAt: string | null; expired: boolean; botAccepts: boolean }): QuestionStatus {
  if (q.chosen) return 'answered'
  if (q.closedAt || q.expired || !q.botAccepts) return 'closed'
  return 'open'
}

/**
 * Why a question or checklist can no longer be answered, in its words: its bot closed it (and which), its time ran out
 * (and when), or the bot can no longer hear answers.
 */
export function endedCaption(q: { closedAt: string | null; expireAt: string | null; expired: boolean; botName: string | null }): string {
  if (q.closedAt) return q.botName ? `Closed by ${q.botName}` : 'Closed'
  if (q.expired && q.expireAt) return `Expired ${formatClock(q.expireAt)}`
  return 'Closed'
}

/** A bot's checklist: items ticked as they go, then Done. Options without a kind (cached before checklists) are a question. */
export function isChecklist(message: Message): boolean {
  return message.optionsKind === 'checklist'
}

/** The Done reply's words as the server writes them, shown while it is still sending (the server also shortens very long reasons). */
export function checklistSummary(checklist: Message, reasons: ChecklistReasonInput[]): string {
  const items = checklist.options ?? []
  const lines = items.flatMap((item) => {
    const given = reasons.find((r) => r.itemId === item.id)
    return given ? [`${item.label}: ${given.reason.trim()}`] : []
  })
  return [`Done ${items.length - reasons.length} of ${items.length}`, ...lines].join('\n')
}
